import type { TranslationKeys } from '~/locales/i18next'

interface ScheduleQueryError {
  code?: string
  message?: string
}

type TranslateFn = (
  key: TranslationKeys,
  defaultValue: string,
  options?: Record<string, unknown>,
) => string

const DEFAULT_QUERY_MESSAGE_PATTERNS = [
  /^Failed to fetch schedules$/,
  /^Failed to fetch schedule \d+$/,
  /^Failed to fetch events for schedule \d+$/,
  /^Failed to fetch schedule holidays$/,
  /^Failed to create schedule$/,
  /^Failed to update schedule \d+$/,
  /^Failed to create schedule event$/,
  /^Failed to update schedule event \d+$/,
  /^Failed to delete schedule event \d+$/,
]

const QUERY_ERROR_KEYS: Record<
  string,
  [key: TranslationKeys, fallback: string]
> = {
  fetch_schedules_error: [
    'schedule.queryError.fetchSchedules',
    'Failed to fetch schedules',
  ],
  fetch_schedule_error: [
    'schedule.queryError.fetchSchedule',
    'Failed to fetch schedule',
  ],
  fetch_events_error: [
    'schedule.queryError.fetchEvents',
    'Failed to fetch schedule events',
  ],
  fetch_holidays_error: [
    'schedule.queryError.fetchHolidays',
    'Failed to fetch schedule holidays',
  ],
  create_schedule_error: [
    'schedule.queryError.createSchedule',
    'Failed to create schedule',
  ],
  update_schedule_error: [
    'schedule.queryError.updateSchedule',
    'Failed to update schedule',
  ],
  create_event_error: [
    'schedule.queryError.createEvent',
    'Failed to create schedule event',
  ],
  update_event_error: [
    'schedule.queryError.updateEvent',
    'Failed to update schedule event',
  ],
  delete_event_error: [
    'schedule.queryError.deleteEvent',
    'Failed to delete schedule event',
  ],
  fetch_error: [
    'schedule.queryError.fetchSchedules',
    'Failed to fetch schedules',
  ],
  create_error: [
    'schedule.queryError.createSchedule',
    'Failed to create schedule',
  ],
  update_error: [
    'schedule.queryError.updateSchedule',
    'Failed to update schedule',
  ],
  delete_error: [
    'schedule.queryError.deleteEvent',
    'Failed to delete schedule event',
  ],
}

export function getLocalizedScheduleQueryError({
  error,
  fallbackMessage,
  t,
}: {
  error: ScheduleQueryError
  fallbackMessage?: string
  t: TranslateFn
}): string {
  const mapped = error.code ? QUERY_ERROR_KEYS[error.code] : undefined
  if (mapped) {
    return t(mapped[0], mapped[1])
  }

  if (
    typeof error.message === 'string' &&
    error.message.trim().length > 0 &&
    !DEFAULT_QUERY_MESSAGE_PATTERNS.some((pattern) =>
      pattern.test(error.message as string),
    )
  ) {
    return error.message
  }

  return (
    fallbackMessage ??
    t('schedule.queryError.generic', 'Something went wrong. Please try again.')
  )
}

export function localizeScheduleQueryError<T extends ScheduleQueryError>(
  error: T | null | undefined,
  t: TranslateFn,
): T | null | undefined {
  if (!error) return error

  return {
    ...error,
    message: getLocalizedScheduleQueryError({ error, t }),
  }
}
